import type { Order, OwnerOrder } from "./order-domain";

export const PROFIT_RANGES = [7, 30, 60] as const;
export type ProfitRange = typeof PROFIT_RANGES[number];
export const PROFIT_AVERAGE_DAYS = 7;

export function orderProfit(order: Order | OwnerOrder): number | null {
  if (!("total_price_cents" in order) || order.total_price_cents === null || order.quote_cents === null) return null;
  return order.total_price_cents - order.quote_cents;
}

export interface ProfitSummary {
  profit_cents: number;
  calculated: number;
  incomplete: number;
}

export interface DailyProfitPoint extends ProfitSummary {
  date: string;
  moving_average_cents: number;
}

export interface RecentProfitSummary extends ProfitSummary {
  days: number;
  series: DailyProfitPoint[];
  projected_profit_cents: number | null;
}

export function summarizeProfit(orders: readonly (Order | OwnerOrder)[]): ProfitSummary {
  const summary = { profit_cents: 0, calculated: 0, incomplete: 0 };
  for (const order of orders) {
    const profit = orderProfit(order);
    if (profit === null) summary.incomplete += 1;
    else { summary.profit_cents += profit; summary.calculated += 1; }
  }
  return summary;
}

function shanghaiDay(value: string | Date): number {
  const date = typeof value === "string"
    ? new Date(/(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}+08:00`)
    : value;
  return Math.floor((date.getTime() + 8 * 60 * 60 * 1000) / 86_400_000);
}

export function buildProfitSeries(orders: readonly (Order | OwnerOrder)[], range: ProfitRange = 30, now = new Date()): DailyProfitPoint[] {
  const today = shanghaiDay(now);
  const visibleStart = today - range + 1;
  const historyStart = visibleStart - PROFIT_AVERAGE_DAYS + 1;
  let firstDay: number | null = null;
  const byDay = new Map<number, (Order | OwnerOrder)[]>();
  for (const order of orders) {
    const day = shanghaiDay(order.order_time);
    if (!Number.isFinite(day) || day > today) continue;
    firstDay = firstDay === null ? day : Math.min(firstDay, day);
    if (day < historyStart) continue;
    const bucket = byDay.get(day) ?? [];
    bucket.push(order);
    byDay.set(day, bucket);
  }
  if (firstDay === null) return [];
  const start = Math.max(firstDay, historyStart);
  const history = Array.from({ length: today - start + 1 }, (_, index) => {
    const day = start + index;
    return { date: new Date(day * 86_400_000).toISOString().slice(0, 10), ...summarizeProfit(byDay.get(day) ?? []) };
  });
  return history.map((day, index): DailyProfitPoint => {
    const window = history.slice(Math.max(0, index - PROFIT_AVERAGE_DAYS + 1), index + 1);
    return { ...day, moving_average_cents: window.reduce((sum, point) => sum + point.profit_cents, 0) / window.length };
  }).slice(Math.max(0, visibleStart - start));
}

export function summarizeRecentProfit(orders: readonly (Order | OwnerOrder)[], now = new Date()): RecentProfitSummary {
  const series = buildProfitSeries(orders, 30, now);
  const days = series.length;
  const summary = series.reduce<ProfitSummary>((total, day) => ({
    profit_cents: total.profit_cents + day.profit_cents,
    calculated: total.calculated + day.calculated,
    incomplete: total.incomplete + day.incomplete,
  }), { profit_cents: 0, calculated: 0, incomplete: 0 });
  return {
    ...summary,
    days,
    series,
    projected_profit_cents: days > 0 && days < 30 && summary.calculated > 0
      ? Math.round(summary.profit_cents / days * 30) : null,
  };
}
